import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Article,
    Gear,
    ArrowRight,
    BookOpen,
    Plus,
    X,
    SpinnerGap,
    LinkSimple,
    TextAa,
    EnvelopeSimple,
    Tray,
    Clock,
    ArrowClockwise,
    Lightning,
    Warning,
    CaretDown,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { DS_TYPOGRAPHY } from '@/config/ui-invariants';
import { ProcessingStatusPanel } from '@/components/shared/ProcessingStatusPanel';
import { ContractText } from '@/components/system/ContractUI';
import {
    ingestArticleMulti,
    processArticles,
    getArticleProcessingStatus,
    getPendingArticles,
    checkEmailForArticles,
    getArticleDrafts,
    createArticleDraft,
    updateArticleDraft,
    deleteArticleDraft,
    deleteAllArticleDrafts,
    retryArticle,
    removeArticle,
    overrideArticle,
    getFailedArticles,
    getRecentActivity,
} from '@/lib/api';
import { IntentPill } from '@/components/shared/IntentPill';
import { toast } from 'sonner';
import { checkEmailErrorMessage } from '@/lib/checkEmailError';
import { ROUTES } from '@/config/product-contract';
import { AGENT_CONFIG, SHARED_UI_LABELS, ARTICLE_PROVENANCE, ARTICLE_PROVENANCE_DEFAULT } from '@/config/task-display-config';

// WI-ARTICLE-OUTCOME-CLASS-SSOT-001: retryable outcome classes
// (gate_blocked_* re-fail deterministically — hide Retry for those)
const RETRYABLE_OUTCOME_CLASSES = ['analysis_failed', 'fetch_failed', 'extraction_empty'];
const GATE_BLOCKED_LANGUAGE_CLASS = 'gate_blocked_language';

/**
 * Article Agent — Row-Based Input + Pending + Processing Status
 * Authority: docs/agents/article/ARTICLE_AGENT_SPEC.md v2.0
 *
 * Route: /agents/article-agent
 * Sidebar: Intelligence section
 *
 * Three sections:
 *   1. Add Articles (URL + text rows, Analyze All, Check Email)
 *   2. Pending Articles (resume analysis for previously saved articles)
 *   3. Processing Status (stepper + business-facing error messages)
 */

let _rowKeyCounter = 0;
const createEmptyRow = () => ({
    url: '', rawText: '', draftId: null, saved: false,
    _key: `row-${++_rowKeyCounter}-${Date.now()}`,
});

const SECTOR_INTELLIGENCE_STEPS = [
    { id: 'reading', label: SHARED_UI_LABELS.articleStepReading },
    { id: 'dedup', label: SHARED_UI_LABELS.articleStepDedup },
    { id: 'analyzing', label: SHARED_UI_LABELS.articleStepAnalyzing },
    { id: 'preparing', label: SHARED_UI_LABELS.articleStepPreparing },
];

// Max time (ms) before the frontend stops polling for processing status.
// Prevents indefinite polling if the backend hangs.
const POLL_MAX_MS = 600 * 1000; // 10 minutes — matches ARTICLE_FRONTEND_POLL_MAX_SECONDS

function ArticleRow({ row, index, onChange, onRemove, onBlur, showRemove, disabled }) {
    const hasUrl = !!row.url.trim();
    const hasText = !!row.rawText.trim();
    return (
        <div className="relative bg-white rounded-lg border border-slate-200 p-5">
            {/* Remove button — top right */}
            {showRemove && (
                <button
                    onClick={() => onRemove(index)}
                    disabled={disabled}
                    className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-white text-slate-500 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors disabled:opacity-50 text-base font-bold"
                    title={SHARED_UI_LABELS.removeSourceTitle}
                    aria-label={SHARED_UI_LABELS.removeSourceLabel}
                >
                    ✕
                </button>
            )}

            {/* URL Input */}
            <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    🔗 URL
                </label>
                <input
                    type="url"
                    value={row.url}
                    onChange={(e) => onChange(index, 'url', e.target.value)}
                    onBlur={() => onBlur(index)}
                    placeholder="https://techcrunch.com/..."
                    disabled={disabled || hasText}
                    className={`w-full px-4 py-2.5 border-2 border-slate-200 rounded-md text-base outline-none transition-colors min-h-[40px] ${
                        hasText ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : row.url.trim() ? 'bg-white text-slate-800' : 'bg-slate-50 text-slate-800 focus:bg-white'
                    } placeholder:text-slate-400 focus:border-brand-900 disabled:cursor-not-allowed`}
                />
            </div>

            {/* OR divider */}
            <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs font-semibold text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Text Input */}
            <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    📝 Paste text
                </label>
                <textarea
                    value={row.rawText}
                    onChange={(e) => onChange(index, 'rawText', e.target.value)}
                    onBlur={() => onBlur(index)}
                    placeholder={SHARED_UI_LABELS.pasteArticleText}
                    rows={2}
                    disabled={disabled || hasUrl}
                    className={`w-full px-4 py-2.5 border-2 border-slate-200 rounded-md text-base outline-none resize-y transition-colors font-[inherit] ${
                        hasUrl ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : row.rawText.trim() ? 'bg-white text-slate-800' : 'bg-slate-50 text-slate-800 focus:bg-white'
                    } placeholder:text-slate-400 focus:border-brand-900 disabled:cursor-not-allowed`}
                />
            </div>
        </div>
    );
}

function PaywallPasteRow({ rowIndex, isSubmitting, onSubmit }) {
    const [text, setText] = useState('');
    const handleSubmit = () => {
        const trimmed = text.trim();
        if (!trimmed) {
            toast.error(SHARED_UI_LABELS.pasteTextFallbackEmpty);
            return;
        }
        onSubmit(rowIndex, trimmed);
    };
    return (
        <div className="mt-3 pt-3 border-t border-red-200">
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={SHARED_UI_LABELS.pasteTextFallbackPlaceholder}
                rows={4}
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-md text-base outline-none resize-y transition-colors font-[inherit] bg-white text-slate-800 placeholder:text-slate-400 focus:border-brand-900 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            />
            <div className="mt-2 flex justify-end">
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !text.trim()}
                    className="bg-brand-900 hover:bg-brand-800 text-white font-semibold min-h-[44px] px-4"
                >
                    {isSubmitting
                        ? SHARED_UI_LABELS.pasteTextFallbackSubmitting
                        : SHARED_UI_LABELS.pasteTextFallbackButton}
                </Button>
            </div>
        </div>
    );
}

function PendingArticleCard({ article }) {
    const formattedDate = article.created_at
        ? new Date(article.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
        : '';
    // WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13: surface how this row got
    // here — the CEO submitted it himself, or it arrived on its own (e.g. the
    // daily Inc42 harvest). Without this, harvested rows were indistinguishable
    // from his own submissions in this exact list. See docs/findings/ONE_LINK_ONE_ARTICLE_2026-08-12.md.
    const provenance = ARTICLE_PROVENANCE[article.input_channel] || ARTICLE_PROVENANCE_DEFAULT;
    return (
        // Row bg is white, not the slate-50 this row used before: DESIGN_INTENTS.neutral
        // (used by the provenance pill above and, pre-existing, by every "Waiting"-status
        // pill in Recent Activity below) resolves to bg-slate-50 text-slate-700 — on a
        // slate-50 row that pill's background was indistinguishable from the row itself,
        // failing rule 15 (strong contrast, no subtle greys) for a visually impaired
        // reader. White + border-slate-200 matches DS_BORDERS.color (#E5E7EB), the
        // documented card token, and gives every intent (including neutral) a background
        // it can actually be seen against. Team-lead-flagged 2026-08-13.
        <div className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200">
            <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-slate-800 truncate">{article.title}</p>
                <p className="text-sm text-slate-500 mt-0.5">
                    {article.source && <span>{article.source}</span>}
                    {article.source && formattedDate && <span className="mx-1.5">·</span>}
                    {formattedDate && <span>Added {formattedDate}</span>}
                </p>
            </div>
            <div className="flex items-center gap-3 shrink-0 ml-3">
                <IntentPill intent={provenance.intent} label={provenance.label} />
                <Clock weight="regular" className="w-5 h-5 text-slate-400" />
            </div>
        </div>
    );
}

function relativeTime(isoString) {
    if (!isoString) return '';
    const now = Date.now();
    const then = new Date(isoString).getTime();
    if (isNaN(then)) return '';
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 2) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
}

// WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13: shared by handleAnalyzeAll
// and handleResumeAnalysis. POST /articles/process splits rather than drops
// when a run exceeds ARTICLE_MAX_BATCH_SIZE — the excess stays pending
// (surfaced separately via the Waiting for Review refresh), but the CEO asked
// for N and only got fewer, so that difference is stated explicitly rather
// than left for him to notice on his own. cappedCount is response.batch_capped_count.
function describeAnalyzingRun(analyzingCount, cappedCount) {
    const base = `Analyzing ${analyzingCount} article${analyzingCount === 1 ? '' : 's'}`;
    if (!cappedCount) return `${base}...`;
    return `${base} now — ${cappedCount} ${SHARED_UI_LABELS.articleBatchCappedSuffix}`;
}

export default function ArticleAgent() {
    const [rows, setRows] = useState([createEmptyRow()]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const [steps, setSteps] = useState([]);
    const [pendingArticles, setPendingArticles] = useState([]);
    const [processResult, setProcessResult] = useState(null);
    const [ingestionResults, setIngestionResults] = useState([]);
    const [failedArticles, setFailedArticles] = useState([]);
    const [retryingId, setRetryingId] = useState(null);
    const [acceptingId, setAcceptingId] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [addSourcesOpen, setAddSourcesOpen] = useState(true);
    const [needsAttentionOpen, setNeedsAttentionOpen] = useState(true);
    const [recentActivityOpen, setRecentActivityOpen] = useState(true);
    const [processingOpen, setProcessingOpen] = useState(true);

    const pollIntervalRef = useRef(null);

    // Cleanup poll interval on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    // Load drafts on mount (persistent draft state)
    useEffect(() => {
        getArticleDrafts()
            .then(data => {
                const drafts = data?.drafts || [];
                if (drafts.length > 0) {
                    setRows(drafts.map(d => ({
                        url: d.url || '',
                        rawText: d.raw_text || '',
                        draftId: d.id,
                        saved: true,
                    })));
                }
            })
            .catch(() => {});
    }, []);

    // Load pending articles on mount
    useEffect(() => {
        getPendingArticles()
            .then(data => setPendingArticles(data?.articles || []))
            .catch(() => setPendingArticles([]));
    }, []);

    // Load failed articles on mount
    useEffect(() => {
        getFailedArticles()
            .then(data => setFailedArticles(data?.articles || []))
            .catch(() => setFailedArticles([]));
    }, []);

    // Load recent activity on mount
    useEffect(() => {
        getRecentActivity()
            .then(data => setRecentActivity(data?.articles || []))
            .catch(() => setRecentActivity([]));
    }, []);

    const filledRowCount = rows.filter(r => r.url.trim() || r.rawText.trim()).length;

    const handleRowChange = useCallback((index, field, value) => {
        setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value, saved: false } : r));
    }, []);

    const addRow = useCallback(() => {
        setRows(prev => [...prev, createEmptyRow()]);
    }, []);

    const resetAll = useCallback(() => {
        deleteAllArticleDrafts().catch(() => {});
        setRows([createEmptyRow()]);
        setIngestionResults([]);
        setProcessResult(null);
        setSteps([]);
        toast.success('Cleared — ready for new articles');
    }, []);

    const removeRow = useCallback((index) => {
        const row = rows[index];
        if (row?.draftId) {
            deleteArticleDraft(row.draftId).catch(() => {});
        }
        setRows(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated.length === 0 ? [createEmptyRow()] : updated;
        });
    }, [rows]);

    const saveDraft = useCallback(async (index) => {
        const row = rows[index];
        if (!row) return;
        // Only save if there's content
        if (!row.url.trim() && !row.rawText.trim()) return;

        try {
            if (row.draftId) {
                await updateArticleDraft(row.draftId, {
                    url: row.url.trim(),
                    raw_text: row.rawText.trim(),
                });
            } else {
                const result = await createArticleDraft({
                    url: row.url.trim(),
                    raw_text: row.rawText.trim(),
                });
                if (result?.id) {
                    setRows(prev => prev.map((r, i) =>
                        i === index ? { ...r, draftId: result.id, saved: true } : r
                    ));
                    return;
                }
            }
            setRows(prev => prev.map((r, i) =>
                i === index ? { ...r, saved: true } : r
            ));
        } catch (e) {
            // WI-INVARIANT-UI-SURFACE-REPAIR-001 (PKG-009): saving a draft is a
            // user-initiated action — a silent failure left the CEO's typed
            // article unsaved with zero feedback. Surface it (reuse the sonner
            // `toast` already imported at the top; no new component).
            console.error('Draft save failed:', e);
            toast.error('Could not save your draft — please try again.');
        }
    }, [rows]);

    const updateStep = useCallback((stepId, status, detail) => {
        setSteps(prev => prev.map(s =>
            s.id === stepId ? { ...s, status, detail: detail || s.detail } : s
        ));
    }, []);

    // Business-facing error message mapper
    const friendlyError = (raw) => {
        if (!raw) return SHARED_UI_LABELS.friendlyErrorDefault;
        const lower = raw.toLowerCase();
        if (lower.includes('credit') || lower.includes('billing')) return SHARED_UI_LABELS.friendlyErrorBilling;
        if (lower.includes('timeout')) return SHARED_UI_LABELS.friendlyErrorTimeout;
        if (lower.includes('paywall')) return SHARED_UI_LABELS.paywallGenericMessage;
        if (lower.includes('too short')) return SHARED_UI_LABELS.friendlyErrorTooShort;
        return SHARED_UI_LABELS.friendlyErrorDefault;
    };

    const refreshFailedArticles = useCallback(() => {
        getFailedArticles()
            .then(data => setFailedArticles(data?.articles || []))
            .catch(() => setFailedArticles([]));
        getRecentActivity()
            .then(data => setRecentActivity(data?.articles || []))
            .catch(() => setRecentActivity([]));
    }, []);

    const handleAnalyzeAll = useCallback(async () => {
        const filledRows = rows.filter(r => r.url.trim() || r.rawText.trim());
        if (filledRows.length === 0) {
            toast.error('Add at least one article before analyzing');
            return;
        }

        setIsProcessing(true);
        setProcessResult(null);
        setIngestionResults([]);
        setSteps(SECTOR_INTELLIGENCE_STEPS.map(s => ({ ...s, status: 'pending' })));

        try {
            // Step 1: Ingest
            updateStep('reading', 'active');
            const articles = filledRows.map(r => ({
                url: r.url.trim() || null,
                raw_text: r.rawText.trim() || null,
                title: null,
            })).filter(a => a.url || a.raw_text);
            const ingestData = await ingestArticleMulti(articles);
            const ingested = ingestData?.ingested || 0;
            const duplicates = ingestData?.duplicates || 0;
            updateStep('reading', 'completed', `${ingestData?.total || 0} articles read`);

            // Step 2: Dedup — capture per-article results for display.
            // WI-ARTICLE-STUCK-STATE-RESILIENCE-001: pass kb_entry_id and
            // matched_status through so the row can deeplink to the existing
            // KB entry on a duplicate outcome ("Already in KB — open").
            const perArticleResults = (ingestData?.results || []).map((r, i) => ({
                title: r.title || filledRows[i]?.url?.trim() || `Article ${i + 1}`,
                outcome: r.outcome || r.status || 'unknown_error',
                message: r.message || r.reason || 'Unknown result',
                severity: r.severity || 'info',
                fetchMethod: r.fetch_method || null,
                fetchStatus: r.fetch_status || null,
                source: r.source || null,
                url: r.url || filledRows[i]?.url?.trim() || null,
                kbEntryId: r.kb_entry_id || null,
                matchedStatus: r.matched_status || null,
                articleId: r.article_id || r.matched_id || null,
                pasteSubmitting: false,
            }));
            setIngestionResults(perArticleResults);

            // WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13: only rows that
            // actually created a new pending article carry an articleId (the
            // backend returns article_id: null for duplicates) — this is
            // exactly the set the CEO just submitted, never the whole queue.
            // See docs/findings/ONE_LINK_ONE_ARTICLE_2026-08-12.md.
            const justIngestedIds = perArticleResults
                .filter(r => r.articleId)
                .map(r => r.articleId);

            const rejected = ingestData?.rejected || 0;
            updateStep('dedup', 'completed',
                ingested > 0
                    ? `${ingested} new, ${duplicates} duplicate${duplicates !== 1 ? 's' : ''}`
                    : rejected > 0
                        ? `${rejected} could not be added — see details below`
                        : `${duplicates} duplicate${duplicates !== 1 ? 's' : ''}`
            );

            // Clear input rows immediately after successful ingestion (Task 8)
            // Analysis runs in background — no need to keep draft rows visible
            if (ingested > 0) {
                setRows([createEmptyRow()]);
                deleteAllArticleDrafts().catch(() => {});
            }

            if (ingested === 0) {
                updateStep('analyzing', 'completed', 'No new articles to analyze');
                updateStep('preparing', 'completed', 'Nothing to send');
                if (rejected > 0) {
                    toast.error('Some articles could not be added — check the details below');
                } else if (duplicates > 0) {
                    toast.info('All articles were already in your queue');
                } else {
                    toast.info('No articles to analyze');
                }
                setIsProcessing(false);
                return;
            }

            // Step 3: Kick off background analysis — returns immediately.
            // WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13: scope the run to
            // just the articles this click ingested. POST /articles/process now
            // REQUIRES article_ids — a call with none is a named refusal, not a
            // fallback to "process everything" — so this is no longer optional.
            // The wording below uses the frontend's own count of just-submitted
            // ids rather than response.total, so it stays correct even if the
            // two ever diverge.
            updateStep('analyzing', 'active');
            const response = await processArticles(justIngestedIds);
            const analyzingCount = justIngestedIds.length || response.total || 0;

            // "Nothing silently strands": refresh the Waiting for Review queue
            // immediately so anything NOT scoped into this run — including rows
            // an automated harvest parked earlier the same session — is visibly
            // waiting the moment the CEO clicks Analyze, not just once this run
            // finishes minutes later. Provenance for each row comes from this
            // same GET (input_channel), never from the process response, which
            // does not carry channel info. See team-lead sync 2026-08-13.
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});

            if (response.status === 'started') {
                const cappedCount = response.batch_capped_count || 0;
                updateStep('analyzing', 'active', describeAnalyzingRun(analyzingCount, cappedCount));
                // Stated once, at the moment it happens — the queue refresh above
                // shows the capped rows are still there, but not why they weren't
                // included, so this is said explicitly rather than left to infer.
                if (cappedCount > 0) {
                    toast.info(describeAnalyzingRun(analyzingCount, cappedCount));
                }

                // Poll for completion. WI-ARTICLE-STUCK-STATE-RESILIENCE-001:
                // when the backend reports stuck_count > 0 we hand off to the
                // stuck-processing reaper instead of telling the CEO the run
                // failed — those articles will auto-recover within the window.
                const pollStartTime = Date.now();
                pollIntervalRef.current = setInterval(async () => {
                    // Guard: stop polling after POLL_MAX_MS to prevent indefinite hang.
                    if (Date.now() - pollStartTime > POLL_MAX_MS) {
                        clearInterval(pollIntervalRef.current);
                        let lastStatus = null;
                        try { lastStatus = await getArticleProcessingStatus(); } catch (_) {}
                        const stuckCount = lastStatus?.stuck_count || 0;
                        const stuckMinutes = Math.round((lastStatus?.stuck_window_seconds || 900) / 60);
                        if (stuckCount > 0) {
                            updateStep('analyzing', 'active', `${stuckCount} article${stuckCount === 1 ? '' : 's'} still analyzing — will auto-recover within ${stuckMinutes} minutes`);
                            toast.info(`${stuckCount} article${stuckCount === 1 ? '' : 's'} are still analyzing in the background. They'll appear in your inbox automatically once ready.`);
                        } else {
                            updateStep('analyzing', 'active', 'Analysis still running in background — check inbox shortly');
                            toast.info('Analysis is still running in the background. Your articles will appear in your inbox once ready.');
                        }
                        setIsProcessing(false);
                        return;
                    }

                    try {
                        const status = await getArticleProcessingStatus();

                        // Update step detail with progress (and surface stuck rows so the
                        // CEO sees the reaper hand-off instead of silent processing).
                        let detail = `${status.completed} of ${status.total} analyzed`;
                        if (status.failed > 0) detail += `, ${status.failed} issue${status.failed !== 1 ? 's' : ''}`;
                        if (status.stuck_count > 0) {
                            const stuckMins = Math.round((status.stuck_window_seconds || 900) / 60);
                            detail += ` — ${status.stuck_count} stuck (auto-recover within ${stuckMins}m)`;
                        }
                        updateStep('analyzing', 'active', detail);

                        // Update per-article results for live display
                        if (status.articles && status.articles.length > 0) {
                            setIngestionResults(status.articles.map(a => ({
                                title: a.title,
                                outcome: a.status,
                                message: a.message,
                                severity: a.status === 'done' ? 'success' :
                                         a.status === 'analyzing' ? 'info' :
                                         a.status === 'waiting' ? 'info' :
                                         a.status === 'attention' ? 'warning' : 'error',
                            })));
                        }

                        // Check if processing is complete
                        if (!status.is_processing) {
                            clearInterval(pollIntervalRef.current);

                            if (status.completed > 0) {
                                updateStep('analyzing', 'completed', `${status.completed} analyzed`);
                                updateStep('preparing', 'completed', 'Ready in your inbox');
                                toast.success(`${status.completed} article${status.completed === 1 ? '' : 's'} analyzed and ready for review`);
                            } else {
                                updateStep('analyzing', 'error', 'Could not analyze articles');
                                updateStep('preparing', 'error', 'Could not prepare reviews');
                                toast.error('Analysis service could not process articles');
                            }

                            setProcessResult({
                                analyzed: status.completed,
                                rejected: 0,
                                errors: status.failed,
                                error_details: status.articles
                                    .filter(a => a.status === 'error')
                                    .map(a => a.message),
                            });

                            setIsProcessing(false);
                            getPendingArticles()
                                .then(data => setPendingArticles(data?.articles || []))
                                .catch(() => {});
                            refreshFailedArticles();
                        }
                    } catch (pollError) {
                        // Polling failure is non-fatal — just keep trying
                        console.warn('Status poll failed:', pollError);
                    }
                }, 10000); // Poll every 10 seconds

                // Exit early — the poll handler controls isProcessing from here
                return;
            }

            // Fallback: response was not "started" (e.g. in_progress or completed with 0)
            const analyzed = response?.completed || response?.analyzed || 0;
            const errors = response?.failed || response?.errors || 0;

            if (analyzed > 0) {
                updateStep('analyzing', 'completed', `${analyzed} analyzed`);
                updateStep('preparing', 'completed', 'Ready in your inbox');
                toast.success(`${analyzed} article${analyzed === 1 ? '' : 's'} analyzed and ready for review`);
            } else if (errors > 0) {
                const errorMsg = response?.error_details?.[0] || response?.message || 'Analysis service temporarily unavailable';
                updateStep('analyzing', 'error', errorMsg);
                updateStep('preparing', 'error', 'Could not prepare reviews');
                toast.error(errorMsg);
            } else {
                updateStep('analyzing', 'completed', 'No articles needed analysis');
                updateStep('preparing', 'completed', 'Nothing to prepare');
            }

            setProcessResult(response);

        } catch (e) {
            console.error('Article processing failed:', e);
            // api.js surfaces the backend's business-facing outcome message on
            // e.message (e.g. "No articles were selected to analyze") — prefer
            // it over the generic fallback so the CEO hears why, not just that.
            toast.error(e.message || 'Something went wrong. Please try again.');
            setSteps(prev => prev.map(s =>
                s.status === 'active' ? { ...s, status: 'error', detail: 'Failed' } : s
            ));
            setIsProcessing(false);
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});
            refreshFailedArticles();
        }
    }, [rows, updateStep, refreshFailedArticles]);

    // ── Piano paywall recovery — inline paste path ──
    // Replaces the failed row's content in-place. Reuses the existing
    // ingest-multi endpoint with raw_text only; the fetcher is bypassed
    // entirely so Piano cannot block the second pass. Title is carried from
    // the original failed row so the article retains identity in the KB.
    const handlePastedTextSubmit = useCallback(async (rowIndex, pastedText) => {
        setIngestionResults(prev => prev.map((r, j) =>
            j === rowIndex ? { ...r, pasteSubmitting: true } : r
        ));
        try {
            const originalRow = ingestionResults[rowIndex];
            const payload = [{
                url: null,
                raw_text: pastedText,
                title: originalRow?.title || null,
            }];
            const ingestData = await ingestArticleMulti(payload, 'manual_upload');
            const r = (ingestData?.results || [])[0];
            if (!r) {
                throw new Error('ingest-multi returned no result for pasted text');
            }
            setIngestionResults(prev => prev.map((existing, j) =>
                j === rowIndex
                    ? {
                          ...existing,
                          outcome: r.outcome || r.status || 'unknown_error',
                          message: r.message || r.reason || SHARED_UI_LABELS.pasteTextFallbackSuccess,
                          severity: r.severity || 'success',
                          fetchMethod: r.fetch_method || 'paste',
                          fetchStatus: r.fetch_status || null,
                          articleId: r.article_id || r.matched_id || existing.articleId,
                          pasteSubmitting: false,
                      }
                    : existing
            ));
            if (r.severity === 'error') {
                toast.error(r.message || SHARED_UI_LABELS.pasteTextFallbackError);
            } else {
                toast.success(SHARED_UI_LABELS.pasteTextFallbackSuccess);
            }
            refreshFailedArticles();
        } catch (e) {
            setIngestionResults(prev => prev.map((r, j) =>
                j === rowIndex ? { ...r, pasteSubmitting: false } : r
            ));
            toast.error(SHARED_UI_LABELS.pasteTextFallbackError);
        }
    }, [ingestionResults, refreshFailedArticles]);

    const handleResumeAnalysis = useCallback(async () => {
        // WI-TB-CONTENT-RULINGS-IMPLEMENTATION-2026-08-13: POST /articles/process
        // no longer has an unscoped "process everything" fallback — a call with
        // no article_ids is a named refusal, not the whole queue. "Resume
        // Analysis" is the one button whose entire purpose IS the whole queue,
        // so it now explicitly passes every waiting article's id.
        const allPendingIds = pendingArticles.map(a => a.article_id).filter(Boolean);
        if (allPendingIds.length === 0) {
            toast.info('Nothing is waiting to be analyzed');
            return;
        }

        setIsResuming(true);
        setProcessResult(null);
        setIngestionResults([]);
        setSteps(SECTOR_INTELLIGENCE_STEPS.map(s => ({ ...s, status: 'pending' })));
        updateStep('reading', 'completed', `${pendingArticles.length} articles waiting`);
        updateStep('dedup', 'completed', 'Previously checked');

        try {
            updateStep('analyzing', 'active');
            const response = await processArticles(allPendingIds);
            const analyzingCount = allPendingIds.length || response.total || 0;

            // Refresh the queue immediately in case anything arrived on its own
            // between loading this list and clicking the button — same
            // "nothing silently strands" reasoning as handleAnalyzeAll.
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});

            if (response.status === 'started') {
                const cappedCount = response.batch_capped_count || 0;
                updateStep('analyzing', 'active', describeAnalyzingRun(analyzingCount, cappedCount));
                if (cappedCount > 0) {
                    toast.info(describeAnalyzingRun(analyzingCount, cappedCount));
                }

                // Poll for completion. Mirrors handleAnalyzeAll loop — surfaces
                // stuck_count from the Mongo-derived /processing-status so the
                // stuck-processing reaper hand-off is visible to the CEO.
                const resumePollStart = Date.now();
                pollIntervalRef.current = setInterval(async () => {
                    // Guard: stop polling after POLL_MAX_MS
                    if (Date.now() - resumePollStart > POLL_MAX_MS) {
                        clearInterval(pollIntervalRef.current);
                        let lastStatus = null;
                        try { lastStatus = await getArticleProcessingStatus(); } catch (_) {}
                        const stuckCount = lastStatus?.stuck_count || 0;
                        const stuckMinutes = Math.round((lastStatus?.stuck_window_seconds || 900) / 60);
                        if (stuckCount > 0) {
                            updateStep('analyzing', 'active', `${stuckCount} article${stuckCount === 1 ? '' : 's'} still analyzing — will auto-recover within ${stuckMinutes} minutes`);
                            toast.info(`${stuckCount} article${stuckCount === 1 ? '' : 's'} are still analyzing in the background. They'll appear in your inbox automatically once ready.`);
                        } else {
                            updateStep('analyzing', 'active', 'Analysis still running in background — check inbox shortly');
                            toast.info('Analysis is still running in the background. Your articles will appear in your inbox once ready.');
                        }
                        setIsResuming(false);
                        return;
                    }

                    try {
                        const status = await getArticleProcessingStatus();

                        // Update step detail with progress (+ stuck-row affordance).
                        let detail = `${status.completed} of ${status.total} analyzed`;
                        if (status.failed > 0) detail += `, ${status.failed} issue${status.failed !== 1 ? 's' : ''}`;
                        if (status.stuck_count > 0) {
                            const stuckMins = Math.round((status.stuck_window_seconds || 900) / 60);
                            detail += ` — ${status.stuck_count} stuck (auto-recover within ${stuckMins}m)`;
                        }
                        updateStep('analyzing', 'active', detail);

                        // Update per-article results for live display
                        if (status.articles && status.articles.length > 0) {
                            setIngestionResults(status.articles.map(a => ({
                                title: a.title,
                                outcome: a.status,
                                message: a.message,
                                severity: a.status === 'done' ? 'success' :
                                         a.status === 'analyzing' ? 'info' :
                                         a.status === 'waiting' ? 'info' :
                                         a.status === 'attention' ? 'warning' : 'error',
                            })));
                        }

                        // Check if processing is complete
                        if (!status.is_processing) {
                            clearInterval(pollIntervalRef.current);

                            if (status.completed > 0) {
                                updateStep('analyzing', 'completed', `${status.completed} analyzed`);
                                updateStep('preparing', 'completed', 'Ready in your inbox');
                                toast.success(`${status.completed} article${status.completed === 1 ? '' : 's'} analyzed and ready for review`);
                            } else {
                                updateStep('analyzing', 'error', 'Could not analyze articles');
                                updateStep('preparing', 'error', 'Could not prepare reviews');
                                toast.error('Analysis service could not process articles');
                            }

                            setProcessResult({
                                analyzed: status.completed,
                                rejected: 0,
                                errors: status.failed,
                                error_details: status.articles
                                    .filter(a => a.status === 'error')
                                    .map(a => a.message),
                            });

                            setIsResuming(false);
                            getPendingArticles()
                                .then(data => setPendingArticles(data?.articles || []))
                                .catch(() => {});
                            refreshFailedArticles();
                        }
                    } catch (pollError) {
                        // Polling failure is non-fatal — just keep trying
                        console.warn('Status poll failed:', pollError);
                    }
                }, 10000); // Poll every 10 seconds

                // Exit early — the poll handler controls isResuming from here
                return;
            }

            // Fallback: not "started"
            const analyzed = response?.completed || response?.analyzed || 0;
            const errors = response?.failed || response?.errors || 0;

            if (analyzed > 0) {
                updateStep('analyzing', 'completed', `${analyzed} analyzed`);
                updateStep('preparing', 'completed', 'Ready in your inbox');
                toast.success(`${analyzed} article${analyzed === 1 ? '' : 's'} analyzed and ready for review`);
            } else if (errors > 0) {
                const errorMsg = response?.error_details?.[0] || response?.message || 'Analysis service temporarily unavailable';
                updateStep('analyzing', 'error', errorMsg);
                updateStep('preparing', 'error', 'Could not prepare reviews');
                toast.error(errorMsg);
            } else {
                updateStep('analyzing', 'completed', 'No articles needed analysis');
                updateStep('preparing', 'completed', 'Nothing to prepare');
            }

            setProcessResult(response);
            setIsResuming(false);
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});
        } catch (e) {
            // See handleAnalyzeAll's matching catch — prefer the backend's
            // business-facing outcome message when api.js surfaced one.
            toast.error(e.message || 'Something went wrong. Please try again.');
            setSteps(prev => prev.map(s =>
                s.status === 'active' ? { ...s, status: 'error', detail: 'Failed' } : s
            ));
            setIsResuming(false);
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});
            refreshFailedArticles();
        }
    }, [pendingArticles, updateStep, refreshFailedArticles]);

    const handleCheckEmail = useCallback(async () => {
        setIsCheckingEmail(true);
        try {
            const result = await checkEmailForArticles();
            const ingested = result?.articles_ingested || 0;
            const found = result?.articles_found || 0;

            if (ingested > 0) {
                toast.success(`Found ${ingested} new article${ingested === 1 ? '' : 's'} from email`);
            } else if (found > 0) {
                toast.info(`Found ${found} articles but all were duplicates or unreadable`);
            } else {
                toast.info('No new articles found in email');
            }

            // Refresh pending
            getPendingArticles()
                .then(data => setPendingArticles(data?.articles || []))
                .catch(() => {});
        } catch (e) {
            // WI-CHECKEMAIL-ERROR-HONESTY-001: a 503 here is the connections-area
            // health gate refusing before the handler runs — the mailbox was never
            // contacted. Blaming email settings for that sends the CEO to debug the
            // wrong system entirely.
            toast.error(checkEmailErrorMessage(e));
        } finally {
            setIsCheckingEmail(false);
        }
    }, []);

    const handleRetry = useCallback(async (articleId) => {
        setRetryingId(articleId);
        try {
            const result = await retryArticle(articleId);
            if (result?.outcome === 'article_retried') {
                toast.success(result.message);
                refreshFailedArticles();
                getPendingArticles()
                    .then(data => setPendingArticles(data?.articles || []))
                    .catch(() => {});

                // If backend auto-started analysis, wire up stepper + polling
                if (result.auto_started) {
                    setProcessResult(null);
                    setIngestionResults([]);
                    setSteps(SECTOR_INTELLIGENCE_STEPS.map(s => ({ ...s, status: 'pending' })));
                    updateStep('reading', 'completed', 'Article queued for retry');
                    updateStep('dedup', 'completed', 'Previously checked');
                    updateStep('analyzing', 'active', 'Re-analyzing...');
                    setIsResuming(true);

                    const retryPollStart = Date.now();
                    pollIntervalRef.current = setInterval(async () => {
                        if (Date.now() - retryPollStart > POLL_MAX_MS) {
                            clearInterval(pollIntervalRef.current);
                            let lastStatus = null;
                            try { lastStatus = await getArticleProcessingStatus(); } catch (_) {}
                            const stuckCount = lastStatus?.stuck_count || 0;
                            const stuckMinutes = Math.round((lastStatus?.stuck_window_seconds || 900) / 60);
                            if (stuckCount > 0) {
                                updateStep('analyzing', 'active', `${stuckCount} article${stuckCount === 1 ? '' : 's'} still analyzing — will auto-recover within ${stuckMinutes} minutes`);
                                toast.info(`${stuckCount} article${stuckCount === 1 ? '' : 's'} are still analyzing in the background.`);
                            } else {
                                updateStep('analyzing', 'active', 'Analysis still running in background — check inbox shortly');
                                toast.info('Analysis is still running in the background.');
                            }
                            setIsResuming(false);
                            return;
                        }

                        try {
                            const status = await getArticleProcessingStatus();
                            let detail = `${status.completed} of ${status.total} analyzed`;
                            if (status.failed > 0) detail += `, ${status.failed} issue${status.failed !== 1 ? 's' : ''}`;
                            if (status.stuck_count > 0) {
                                const stuckMins = Math.round((status.stuck_window_seconds || 900) / 60);
                                detail += ` — ${status.stuck_count} stuck (auto-recover within ${stuckMins}m)`;
                            }
                            updateStep('analyzing', 'active', detail);
                            if (status.articles && status.articles.length > 0) {
                                setIngestionResults(status.articles.map(a => ({
                                    title: a.title,
                                    outcome: a.status,
                                    message: a.message,
                                    severity: a.status === 'done' ? 'success' :
                                             a.status === 'analyzing' ? 'info' :
                                             a.status === 'waiting' ? 'info' :
                                             a.status === 'attention' ? 'warning' : 'error',
                                })));
                            }
                            if (!status.is_processing) {
                                clearInterval(pollIntervalRef.current);
                                if (status.completed > 0) {
                                    updateStep('analyzing', 'completed', `${status.completed} analyzed`);
                                    updateStep('preparing', 'completed', 'Ready in your inbox');
                                    toast.success(`${status.completed} article${status.completed === 1 ? '' : 's'} analyzed and ready for review`);
                                } else {
                                    updateStep('analyzing', 'error', 'Could not analyze articles');
                                    updateStep('preparing', 'error', 'Could not prepare reviews');
                                    toast.error('Analysis could not process articles');
                                }
                                setProcessResult({
                                    analyzed: status.completed,
                                    rejected: 0,
                                    errors: status.failed,
                                    error_details: status.articles
                                        .filter(a => a.status === 'error')
                                        .map(a => a.message),
                                });
                                setIsResuming(false);
                                getPendingArticles()
                                    .then(data => setPendingArticles(data?.articles || []))
                                    .catch(() => {});
                                refreshFailedArticles();
                            }
                        } catch (pollError) {
                            console.warn('Status poll failed:', pollError);
                        }
                    }, 10000);
                }
            } else {
                toast.warning(result?.message || 'Could not retry article');
            }
        } catch (e) {
            toast.error('Could not retry article — please try again');
        } finally {
            setRetryingId(null);
        }
    }, [refreshFailedArticles, updateStep]);

    const handleRemove = useCallback(async (articleId) => {
        try {
            const result = await removeArticle(articleId);
            if (result?.outcome === 'article_removed') {
                toast.success(result.message);
                refreshFailedArticles();
            } else {
                toast.warning(result?.message || 'Could not remove article');
            }
        } catch (e) {
            toast.error('Could not remove article — please try again');
        }
    }, [refreshFailedArticles]);

    // WI-ARTICLE-OUTCOME-CLASS-SSOT-001: Accept Anyway for gate_blocked_language rows.
    // Routes through the existing overrideArticle path (POST /api/articles/:id/override).
    // The override endpoint validates reason against ARTICLE_OVERRIDE_REASONS — use
    // 'gate_blocked_language' (must match a value from that list in constants.py).
    const handleAcceptAnyway = useCallback(async (articleId) => {
        setAcceptingId(articleId);
        try {
            const result = await overrideArticle(articleId, 'gate_blocked_language');
            if (result?.status === 'saved') {
                toast.success('Article saved to Knowledge Base');
                refreshFailedArticles();
            } else {
                toast.warning(result?.message || 'Could not accept article');
            }
        } catch (e) {
            toast.error('Could not accept article — please try again');
        } finally {
            setAcceptingId(null);
        }
    }, [refreshFailedArticles]);

    const anyBusy = isProcessing || isResuming;

    // Deduplicate: exclude articles already shown in "Needs Attention"
    const deduplicatedActivity = recentActivity.filter(
        a => !failedArticles.some(f => f.article_id === a.article_id)
    );

    return (
        <>
            {/* Page Header — V7: emoji icon + DM Sans title */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-1">
                    <span className="text-2xl">🔍</span>
                    <h1
                        className="text-[28px] font-extrabold text-brand-900 leading-tight"
                        style={{ fontFamily: DS_TYPOGRAPHY.displayXL.fontFamily }}
                    >
                        Sector Intelligence
                    </h1>
                </div>
                <p className="text-base text-slate-500 mt-1 max-w-2xl leading-relaxed">
                    Add articles by URL or full text. The agent analyzes each one and sends
                    investor-grade insights to your inbox for review.
                </p>
            </div>

            {/* Persistent inbox link */}
            <div className="mb-6">
                <Link
                    to={ROUTES.CEO_OFFICE}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-brand-900 bg-white text-brand-900 text-base font-semibold hover:bg-brand-900 hover:text-white transition-all min-h-[44px]"
                >
                    <Tray weight="bold" className="w-5 h-5" />
                    Go to Inbox
                </Link>
            </div>

            {/* Section 1: Add Articles */}
            <section className="mb-8">
                <button
                    onClick={() => setAddSourcesOpen(!addSourcesOpen)}
                    className="w-full flex items-center justify-between mb-3 pb-1.5 border-b border-slate-200 min-h-[44px]"
                >
                    <h4 className="text-[13px] font-semibold text-slate-500">
                        Add sources
                    </h4>
                    <CaretDown
                        weight="bold"
                        className={`w-4 h-4 text-slate-400 transition-transform ${addSourcesOpen ? '' : '-rotate-90'}`}
                    />
                </button>
                {addSourcesOpen && <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-3">
                    {rows.map((row, i) => (
                        <ArticleRow
                            key={row._key || row.draftId || i}
                            row={row}
                            index={i}
                            onChange={handleRowChange}
                            onRemove={removeRow}
                            onBlur={saveDraft}
                            showRemove={true}
                            disabled={anyBusy}
                        />
                    ))}

                    {/* Add Row + Analyze — V7 dashed add button */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={addRow}
                                disabled={anyBusy}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-dashed border-brand-900 bg-white text-sm font-semibold text-brand-900 hover:bg-brand-900 hover:text-white transition-all min-h-[44px] disabled:opacity-50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                            >
                                + Add another source
                            </button>
                            <button
                                onClick={resetAll}
                                disabled={anyBusy}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-slate-300 bg-white text-sm font-semibold text-slate-600 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-all min-h-[44px] disabled:opacity-50"
                            >
                                ↺ Reset
                            </button>
                        </div>

                        <Button
                            onClick={handleAnalyzeAll}
                            disabled={anyBusy || filledRowCount === 0}
                            className="min-h-[44px] gap-2 bg-brand-900 hover:bg-brand-dark text-white text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed px-8"
                        >
                            {isProcessing ? 'Analyzing...' : 'Analyze'}
                        </Button>
                    </div>

                    {/* Check Email Button */}
                    <div className="pt-3 border-t border-slate-100">
                        <Button
                            onClick={handleCheckEmail}
                            disabled={anyBusy || isCheckingEmail}
                            variant="outline"
                            className="min-h-[44px] gap-2 text-base font-semibold disabled:opacity-60"
                        >
                            {isCheckingEmail ? (
                                <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Checking email...</>
                            ) : (
                                <><EnvelopeSimple weight="bold" className="w-4 h-4" /> Check Email for Articles</>
                            )}
                        </Button>
                    </div>
                </div>}
            </section>

            {/* Section 2: Pending Articles */}
            {pendingArticles.length > 0 && (
                <section className="mb-8">
                    <h2 className="text-lg font-bold text-slate-900 mb-4 font-heading flex items-center gap-2">
                        <Clock weight="fill" className="w-5 h-5 text-amber-500" />
                        Waiting for Review
                    </h2>
                    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-base text-slate-600">
                                {pendingArticles.length} article{pendingArticles.length === 1 ? '' : 's'} saved
                                earlier and waiting for analysis.
                            </p>
                            <Button
                                onClick={handleResumeAnalysis}
                                disabled={anyBusy}
                                className="min-h-[44px] gap-2 bg-white text-slate-600 border border-slate-200 hover:border-brand-900 hover:text-brand-900 text-base font-semibold shadow-sm disabled:opacity-60 px-5"
                            >
                                {isResuming ? (
                                    <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Analyzing...</>
                                ) : (
                                    <><ArrowClockwise weight="bold" className="w-4 h-4" /> Resume Analysis</>
                                )}
                            </Button>
                        </div>

                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {pendingArticles.map((article, i) => (
                                <PendingArticleCard key={article.article_id || i} article={article} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Section: Needs Attention — genuine failures only (WI-ARTICLE-OUTCOME-CLASS-SSOT-001) */}
            {/* Backend now filters out healthy discards (agent_discard/ceo_dismiss) before */}
            {/* returning the list, so every row here is a genuine, actionable failure. */}
            {failedArticles.length > 0 && (
                <section className="mb-8">
                    <button
                        onClick={() => setNeedsAttentionOpen(!needsAttentionOpen)}
                        className="w-full flex items-center justify-between mb-4"
                    >
                        <h2 className="text-lg font-bold text-slate-900 font-heading flex items-center gap-2">
                            <Warning weight="fill" className="w-5 h-5 text-amber-500" />
                            Needs Attention
                        </h2>
                        <CaretDown
                            weight="bold"
                            className={`w-4 h-4 text-slate-400 transition-transform ${needsAttentionOpen ? '' : '-rotate-90'}`}
                        />
                    </button>
                    {needsAttentionOpen && <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm">
                        <p className="text-base text-slate-600 mb-4">
                            {failedArticles.length === 1
                                ? `1 ${SHARED_UI_LABELS.needsAttentionCountOne}`
                                : `${failedArticles.length} ${SHARED_UI_LABELS.needsAttentionCountMany}`}
                        </p>
                        <div className="space-y-2">
                            {failedArticles.map((article) => {
                                const outcomeClass = article.outcome_class || article.error_class || '';
                                // gate_blocked_* rows re-fail deterministically — hide Retry
                                const showRetry = RETRYABLE_OUTCOME_CLASSES.includes(outcomeClass);
                                // gate_blocked_language = save-worthy article needing wording fix
                                const showAcceptAnyway = outcomeClass === GATE_BLOCKED_LANGUAGE_CLASS;
                                const isAccepting = acceptingId === article.article_id;
                                const isRetrying = retryingId === article.article_id;
                                return (
                                    <div
                                        key={article.article_id}
                                        className="flex items-center justify-between px-4 py-3 bg-red-50 rounded-lg border border-red-200"
                                    >
                                        <div className="min-w-0 flex-1 mr-3">
                                            <p className="text-base font-semibold text-slate-800 truncate">
                                                {article.title}
                                            </p>
                                            <p className="text-sm text-red-600 mt-0.5">
                                                {article.error_message}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {showAcceptAnyway && (
                                                <Button
                                                    onClick={() => handleAcceptAnyway(article.article_id)}
                                                    disabled={anyBusy || isAccepting || isRetrying}
                                                    className="min-h-[44px] gap-1.5 bg-brand-900 hover:bg-brand-dark text-white text-sm font-semibold disabled:opacity-60 px-4"
                                                >
                                                    {isAccepting ? (
                                                        <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> {SHARED_UI_LABELS.needsAttentionAccepting}</>
                                                    ) : (
                                                        <>{SHARED_UI_LABELS.needsAttentionAcceptAnyway}</>
                                                    )}
                                                </Button>
                                            )}
                                            {showRetry && (
                                                <Button
                                                    onClick={() => handleRetry(article.article_id)}
                                                    disabled={anyBusy || isRetrying || isAccepting}
                                                    className="min-h-[44px] gap-1.5 bg-brand-900 hover:bg-brand-dark text-white text-sm font-semibold disabled:opacity-60 px-4"
                                                >
                                                    {isRetrying ? (
                                                        <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> {SHARED_UI_LABELS.needsAttentionRetrying}</>
                                                    ) : (
                                                        <><ArrowClockwise weight="bold" className="w-4 h-4" /> {SHARED_UI_LABELS.needsAttentionRetry}</>
                                                    )}
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => handleRemove(article.article_id)}
                                                disabled={anyBusy || isAccepting}
                                                variant="outline"
                                                className="min-h-[44px] gap-1.5 text-red-600 border-red-300 hover:bg-red-50 text-sm font-semibold disabled:opacity-60 px-4"
                                            >
                                                <X weight="bold" className="w-4 h-4" /> {SHARED_UI_LABELS.needsAttentionRemove}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>}
                </section>
            )}

            {/* Section: Recent Activity */}
            <section className="mb-8">
                <button
                    onClick={() => setRecentActivityOpen(!recentActivityOpen)}
                    className="w-full flex items-center justify-between mb-4"
                >
                    <h2 className="text-lg font-bold text-slate-900 font-heading flex items-center gap-2">
                        <Lightning weight="fill" className="w-5 h-5 text-blue-500" />
                        Recent Activity
                    </h2>
                    <CaretDown
                        weight="bold"
                        className={`w-4 h-4 text-slate-400 transition-transform ${recentActivityOpen ? '' : '-rotate-90'}`}
                    />
                </button>
                {recentActivityOpen && <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    {deduplicatedActivity.length === 0 ? (
                        <p className="text-base text-slate-500 text-center py-4">
                            All articles are either in your inbox for review or have been processed. Check your inbox for pending reviews.
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {deduplicatedActivity.map((article) => (
                                <div
                                    key={article.article_id}
                                    // Pre-existing contrast defect, fixed alongside the provenance pill
                                    // above (same collision — see PendingArticleCard's comment): a
                                    // "Waiting"-status row here also renders IntentPill with
                                    // intent="neutral" (ARTICLE_STATUS_INTENT[PENDING], constants.py),
                                    // which is bg-slate-50 — identical to the slate-50 row bg this line
                                    // used to be. Matched to white + border-slate-200 for consistency
                                    // with the fix above; not new scope, same defect. WI-TB-CONTENT-
                                    // RULINGS-IMPLEMENTATION-2026-08-13.
                                    className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200"
                                >
                                    <div className="min-w-0 flex-1 mr-3">
                                        <p className="text-base font-semibold text-slate-800 truncate">
                                            {article.title}
                                        </p>
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            {article.source_label && <span>{article.source_label}</span>}
                                            {article.source_label && article.updated_at && <span className="mx-1.5">·</span>}
                                            {article.updated_at && <span>{relativeTime(article.updated_at)}</span>}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <IntentPill intent={article.intent} label={article.status_label} />
                                        {article.is_retryable && (
                                            <Button
                                                onClick={() => handleRetry(article.article_id)}
                                                disabled={anyBusy || retryingId === article.article_id}
                                                className="min-h-[44px] gap-1.5 bg-brand-900 hover:bg-brand-dark text-white text-sm font-semibold disabled:opacity-60 px-4"
                                            >
                                                {retryingId === article.article_id ? (
                                                    <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Retrying</>
                                                ) : (
                                                    <><ArrowClockwise weight="bold" className="w-4 h-4" /> Retry</>
                                                )}
                                            </Button>
                                        )}
                                        {article.is_removable && (
                                            <Button
                                                onClick={() => handleRemove(article.article_id)}
                                                disabled={anyBusy}
                                                variant="outline"
                                                className="min-h-[44px] gap-1.5 text-red-600 border-red-300 hover:bg-red-50 text-sm font-semibold disabled:opacity-60 px-4"
                                            >
                                                <X weight="bold" className="w-4 h-4" /> Remove
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>}
            </section>

            {/* Section 3: Processing Status */}
            {steps.length > 0 && (
                <section className="mb-8">
                    <button
                        onClick={() => setProcessingOpen(!processingOpen)}
                        className="w-full flex items-center justify-between mb-4"
                    >
                        <h2 className="text-lg font-bold text-slate-900 font-heading flex items-center gap-2">
                            Analysis Progress
                        </h2>
                        <CaretDown
                            weight="bold"
                            className={`w-4 h-4 text-slate-400 transition-transform ${processingOpen ? '' : '-rotate-90'}`}
                        />
                    </button>
                    {processingOpen && <>
                    <ProcessingStatusPanel
                        steps={steps}
                    />

                    {/* Per-Article Ingestion Results */}
                    {ingestionResults.length > 0 && (
                        <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                            <p className="text-sm font-semibold text-slate-700 mb-3">Article Details</p>
                            <div className="space-y-2">
                                {ingestionResults.map((r, i) => (
                                    <div key={i} className={`flex flex-col px-4 py-3 rounded-lg border ${
                                        r.severity === 'success' ? 'bg-emerald-50 border-emerald-200' :
                                        r.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                                        r.severity === 'error' ? 'bg-red-50 border-red-200' :
                                        'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-base font-semibold text-slate-800 truncate">
                                                {r.title}
                                                {r.fetchMethod && (
                                                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium align-middle ${
                                                        r.fetchMethod === 'direct' ? 'bg-emerald-100 text-emerald-700' :
                                                        r.fetchMethod === 'search_fallback' ? 'bg-orange-100 text-orange-700' :
                                                        r.fetchMethod === 'paste' ? 'bg-blue-100 text-blue-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>
                                                        {r.fetchMethod === 'direct' ? 'Direct' :
                                                         r.fetchMethod === 'search_fallback' ? 'Via Search' :
                                                         r.fetchMethod === 'paste' ? 'Pasted' :
                                                         'Failed'}
                                                    </span>
                                                )}
                                            </p>
                                            <p className={`text-sm mt-0.5 ${
                                                r.severity === 'success' ? 'text-emerald-700' :
                                                r.severity === 'warning' ? 'text-amber-700' :
                                                r.severity === 'error' ? 'text-red-700' :
                                                'text-slate-600'
                                            }`}>
                                                {r.message}
                                                {(r.outcome === 'duplicate' || r.outcome === 'duplicate_in_kb') && r.kbEntryId && (
                                                    <Link
                                                        to={`${ROUTES.KNOWLEDGE_BASE}?focus=${r.kbEntryId}`}
                                                        className="ml-2 inline-flex items-center font-semibold underline text-slate-700 hover:text-slate-900"
                                                    >
                                                        View it here
                                                    </Link>
                                                )}
                                            </p>
                                        </div>
                                        {(r.fetchStatus === 'paywall_blocked' ||
                                          (r.fetchStatus === 'error' && r.fetchMethod === 'jina_fallback_failed')) && (
                                            <PaywallPasteRow
                                                rowIndex={i}
                                                isSubmitting={!!r.pasteSubmitting}
                                                onSubmit={handlePastedTextSubmit}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Completion Summary */}
                    {processResult && !anyBusy && (
                        <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                            <ContractText variant="sectionLabel" className="mb-3">Summary</ContractText>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                                    <p className="text-2xl font-bold text-emerald-700">
                                        {processResult.analyzed || 0}
                                    </p>
                                    <p className="text-sm font-medium text-emerald-600">Analyzed</p>
                                </div>
                                <div className="text-center p-3 bg-amber-50 rounded-lg">
                                    <p className="text-2xl font-bold text-amber-700">
                                        {processResult.rejected || 0}
                                    </p>
                                    <p className="text-sm font-medium text-amber-600">Rejected</p>
                                </div>
                                <div className="text-center p-3 bg-red-50 rounded-lg">
                                    <p className="text-2xl font-bold text-red-700">
                                        {processResult.errors || 0}
                                    </p>
                                    <p className="text-sm font-medium text-red-600">Errors</p>
                                </div>
                            </div>

                            {/* Error Details (business-facing) */}
                            {processResult.error_details && processResult.error_details.length > 0 && (
                                <div className="mt-4 space-y-2">
                                    {processResult.error_details.map((detail, i) => (
                                        <p key={i} className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                                            {detail}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {/* Go to Inbox pill — V7: outlined, hover inverts */}
                            {(processResult.analyzed || 0) > 0 && (
                                <div className="flex justify-center mt-6">
                                    <Link
                                        to={ROUTES.CEO_OFFICE}
                                        className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-brand-900 bg-white text-brand-900 text-base font-semibold hover:bg-brand-900 hover:text-white transition-all min-h-[44px]"
                                    >
                                        Go to Inbox →
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                    </>}
                </section>
            )}
        </>
    );
}
